import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable } from 'rxjs';
import { DateOnly, LanguageService, LoaderService, SearchFields, SearchPatientCaseResult, SearchPatientFilters, SearchResult, SearchSortOptions, ToastService, TspPatientRepository } from '../core';
import { FiltersForm } from '../models';
import { SortEvent } from '../shared';
import { BaseService } from './base.service';


/**
 * This service handles patient list.
 */
@Injectable({ providedIn: 'root' })
export class ListService extends BaseService {

	private readonly toastSrv = inject(ToastService);
	private readonly langSrv = inject(LanguageService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly patRepo = inject(TspPatientRepository);

	private readonly _DEFAULT_SORT_ORDER: SearchSortOptions = { name: SearchFields.lastCasePublish, desc: true };
	private readonly _DEFAULT_FILTERS: SearchPatientFilters = {
		page: 1,
		top: 10,
		sortOrder: { ...this._DEFAULT_SORT_ORDER }
	};

	private readonly _searchResult = new BehaviorSubject<SearchResult<SearchPatientCaseResult>>({ items: [], count: 0 });
	private _searchOptions: SearchPatientFilters = { ...this._DEFAULT_FILTERS };


	/**
	* Reset patient list.
	*/
	reset(): void {
		this._searchResult.next({ items: [], count: 0 });
		this._searchOptions = { ...this._DEFAULT_FILTERS };
	}

	/**
	* Get current search result.
	*/
	getSearchResult(): Observable<SearchResult<SearchPatientCaseResult>> {
		return this._searchResult.asObservable();
	}

	/**
	* Update list.
	*/
	search(): void {
		this.loaderSrv.show();
		this.patRepo.search(this._searchOptions).pipe(
			finalize(() => this.loaderSrv.hide()),
			map(res => this._handleApiResponse(res))
		).subscribe({
			next: res => this._searchResult.next(res),
			error: () => this.toastSrv.showError(this.langSrv.labels().FILTERS_COMPONENT_SEARCH_ERROR)
		});
	}

	/**
	 * Update search filters.
	 */
	applyFilters(filters: Partial<FiltersForm>): void {
		this._searchOptions = { ...this._DEFAULT_FILTERS };
		this._searchOptions.userId = filters.userGuid ?? undefined;
		this._searchOptions.patientNumber = filters.patientId ?? undefined;
		this._searchOptions.caseNumber = filters.planId ?? undefined;
		this._searchOptions.frameID = filters.frameId ?? undefined;
		this._searchOptions.treatmentStatus = filters.status ?? undefined;
		this._searchOptions.caseSide = filters.side ?? undefined;
		this._searchOptions.caseBone = filters.boneType ?? undefined;
		this._searchOptions.publishDate = filters.publishDate ? new DateOnly(filters.publishDate) : undefined;
		this._searchOptions.publishDateFilter = filters.publishDate && filters.dateFilter ? filters.dateFilter : undefined;
	}

	/**
	 * Update list by sort order.
	 */
	changeSortOrder(order: SortEvent): void {
		this._searchOptions.sortOrder = order.direction !== '' ? {
			name: order?.column,
			desc: order.direction === 'desc'
		} : undefined;
		this.search();
	}

	/**
	* Update list by page number.
	*/
	changePageNumber(page: number): void {
		this._searchOptions.page = page;
		this.search();
	}

	/**
	* Update list by page size.
	*/
	changePageSize(size: number): void {
		this._searchOptions.page = 1;
		this._searchOptions.top = size;
		this.search();
	}
}
